import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { supportsFigures } from "@/lib/figure-support";
import { RAG_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import {
  deleteSessionDocument,
  getDocument,
  listSessionDocuments,
  refreshSessionSuggestions,
  uploadSessionDocument,
  type DocumentStatus,
} from "@/lib/documents";
import { loadModelConfig } from "@/lib/model-config";
import { withinLoadingWindow } from "@/lib/document-status";

// A chat-dropped document (spec §6/§11). Local, ephemeral state — there is no restore endpoint yet, so the
// chips live for the drawer's session lifetime (reopening a thread does not repopulate them).
interface Attachment {
  id: string;
  name: string;
  status: DocumentStatus | "uploading" | "error";
  /** Providers this doc has embeddings for; a ready doc missing the active one isn't searchable (spec §8). */
  embeddedProviders?: string[];
  /** When ingestion started (ms epoch); bounds status polling so a stuck doc doesn't poll forever (spec §11). */
  startedAt: number;
}

const POLL_MS = 3000;

/**
 * The chat document-drop control (spec §11): an inline XOR chip prompt deciding whether to extract images,
 * then a status chip per attached doc with a remove control. The picker TRIGGER lives in the composer
 * (a paperclip icon button in AssistantDrawer) and calls [ChatAttachmentsHandle.openPicker]; drag-and-drop
 * feeds [acceptFiles]. Gated by the caller (system access). [ensureThreadId] mints + sets the agent thread
 * id when a doc is dropped into a not-yet-started chat, so the upload couples to that session.
 */
export interface ChatAttachmentsHandle {
  /** Queue a dropped/picked file for the with/without-images prompt (used by the drawer's drop target). */
  acceptFiles: (files: FileList) => void;
  /** Open the file picker — wired to the composer's paperclip button. */
  openPicker: () => void;
}

export const ChatAttachments = forwardRef<
  ChatAttachmentsHandle,
  {
    threadId: string | null;
    ensureThreadId: () => string;
    /** Whether the chat drawer is open. Polling pauses while closed (this component stays mounted). */
    open?: boolean;
    /** Push freshly-regenerated suggestion chips into the chat store (spec §7). Wired to `setSuggestions`. */
    onSuggestions?: (items: string[]) => void;
  }
>(function ChatAttachments({ threadId, ensureThreadId, open = true, onSuggestions }, ref) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<File | null>(null); // awaiting the with/without-images choice
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const attachmentsRef = useRef<Attachment[]>([]);
  useEffect(() => {
    attachmentsRef.current = attachments;
  });

  // The active embedding provider, to flag a restored doc that isn't embedded for it (spec §8).
  // Best-effort: this control is already system-access-gated (same audience as /ai/model-config), and a
  // failed/absent config just means no unavailable hint. Fetched once — a provider switch mid-session is
  // rare and the chips restore fresh on the next thread open.
  const [provider, setProvider] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void loadModelConfig()
      .then((c) => {
        if (!cancelled) setProvider(c.provider);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  // A ready doc whose embeddings don't cover the active provider can't be searched — mirrors the admin
  // card's "New processing required" pill. Unknown providers (null config) never flag.
  const unavailable = (a: Attachment) =>
    a.status === "ready" && provider != null && a.embeddedProviders != null && !a.embeddedProviders.includes(provider);

  // Load this session's existing docs so the chips survive a reload / thread switch. Skip the load that
  // follows our OWN mint (a drop into a fresh chat sets the thread id here): the server has nothing yet and
  // reloading would wipe the just-added optimistic chip.
  const skipNextLoadRef = useRef(false);
  useEffect(() => {
    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }
    setPending(null);
    if (!threadId) {
      setAttachments([]);
      return;
    }
    let cancelled = false;
    void listSessionDocuments(threadId)
      .then((docs) => {
        if (!cancelled)
          setAttachments(
            docs.map((d) => ({
              id: d.id,
              name: d.sourceName,
              status: d.status,
              embeddedProviders: d.embeddedProviders,
              startedAt: Date.now(), // fresh window for a still-processing restored doc
            })),
          );
      })
      .catch(() => {}); // best-effort restore; dropping a new doc this session still works
    return () => {
      cancelled = true;
    };
  }, [threadId]);

  const isProcessing = (s: Attachment["status"]) => s === "uploading" || s === "pending" || s === "processing";
  // Keep polling only while something is still ingesting AND within the loading window — a doc stuck past
  // it stops polling rather than spinning forever against a dead pipeline (spec §11; mirrors the admin card).
  const active = attachments.some((a) => isProcessing(a.status) && withinLoadingWindow(a.startedAt));

  // Regenerate the chat's suggestion chips from the attached docs + last message (spec §7). Held in a ref so
  // the poll interval below can call the latest without being torn down when threadId/onSuggestions change.
  const triggerSuggestionRefresh = useCallback(() => {
    if (!threadId || !onSuggestions) return;
    void refreshSessionSuggestions(threadId).then(onSuggestions).catch(() => {});
  }, [threadId, onSuggestions]);
  const refreshRef = useRef(triggerSuggestionRefresh);
  useEffect(() => {
    refreshRef.current = triggerSuggestionRefresh;
  });

  // Poll status while something is ingesting AND the drawer is open. The drawer stays mounted when closed,
  // so gate on `open` to avoid needless background polling; also skip any doc past the loading window so a
  // dead-pipeline doc doesn't poll forever (spec §11). Reads the latest list via ref so the interval is stable.
  useEffect(() => {
    if (!active || !open) return;
    const t = window.setInterval(() => {
      for (const a of attachmentsRef.current) {
        if (a.status !== "pending" && a.status !== "processing") continue;
        if (!withinLoadingWindow(a.startedAt)) continue; // stuck past the window → stop polling this one
        getDocument(a.id)
          .then((doc) => {
            setAttachments((cur) =>
              cur.map((x) => (x.id === a.id ? { ...x, status: doc.status, embeddedProviders: doc.embeddedProviders } : x)),
            );
            // A doc just finished ingesting → refresh the chips so they reflect it (spec §7).
            if (doc.status === "ready" && a.status !== "ready") refreshRef.current();
          })
          .catch(() => {}); // transient; next tick retries
      }
    }, POLL_MS);
    return () => window.clearInterval(t);
  }, [active, open]);

  const startUpload = useCallback(
    (file: File, textOnly: boolean) => {
      setPending(null);
      // A drop into a fresh chat mints the thread id below; that thread-id change must not trigger a
      // restore-load that would clobber this optimistic chip.
      if (threadId === null) skipNextLoadRef.current = true;
      const tid = ensureThreadId();
      const tempId = `tmp-${file.name}-${file.size}-${file.lastModified}`;
      setAttachments((cur) => [...cur, { id: tempId, name: file.name, status: "uploading", startedAt: Date.now() }]);
      void uploadSessionDocument(tid, { file, textOnly })
        .then(({ id }) =>
          setAttachments((cur) => cur.map((x) => (x.id === tempId ? { ...x, id, status: "pending" } : x))),
        )
        .catch(() => setAttachments((cur) => cur.map((x) => (x.id === tempId ? { ...x, status: "error" } : x))));
    },
    [ensureThreadId, threadId],
  );

  const remove = useCallback(
    (a: Attachment) => {
      setAttachments((cur) => cur.filter((x) => x.id !== a.id));
      // Delete server-side (real id, past the optimistic temp), THEN refresh — the suggestion refresh must
      // see the post-delete state, or it would regenerate from the doc that's being removed (spec §7).
      void (async () => {
        if (threadId && !a.id.startsWith("tmp-")) {
          try {
            await deleteSessionDocument(threadId, a.id);
          } catch {
            /* best-effort; refresh anyway */
          }
        }
        triggerSuggestionRefresh();
      })();
    },
    [threadId, triggerSuggestionRefresh],
  );

  // Queue the first file for the with/without-images prompt. Shared by the composer's paperclip button
  // (via openPicker → the hidden input) and the drawer's drag-and-drop target (both through the ref).
  const acceptFiles = useCallback((files: FileList) => {
    const f = files.item(0) ?? null;
    if (f) setPending(f);
  }, []);
  useImperativeHandle(ref, () => ({ acceptFiles, openPicker: () => inputRef.current?.click() }), [acceptFiles]);

  const statusLabel = (s: Attachment["status"]) =>
    intl.formatMessage({ id: `agent.attach.status.${s}`, defaultMessage: s });

  const hasContent = attachments.length > 0 || pending !== null;
  return (
    <>
      {hasContent && (
        <div className="mb-2 flex flex-col gap-2">
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {attachments.map((a) => (
                <span
                  key={a.id}
                  id={`agent-attachment-${a.id}`}
                  className="box-flat inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-smycles-meta text-ink"
                >
                  <span id={`agent-attachment-${a.id}-name`} className="max-w-[12rem] truncate">
                    {a.name}
                  </span>
                  {unavailable(a) ? (
                    <span
                      id={`agent-attachment-${a.id}-state`}
                      className="text-danger"
                      title={intl.formatMessage({
                        id: "agent.attach.unavailableHint",
                        defaultMessage: "Not embedded for the active model. Remove and upload again to use it.",
                      })}
                    >
                      {intl.formatMessage({ id: "agent.attach.unavailable", defaultMessage: "Unavailable for the current model" })}
                    </span>
                  ) : (
                    <span
                      id={`agent-attachment-${a.id}-state`}
                      className={a.status === "failed" || a.status === "error" ? "text-danger" : "text-ink-muted"}
                    >
                      {statusLabel(a.status)}
                    </span>
                  )}
                  <button
                    id={`agent-attachment-${a.id}-remove`}
                    type="button"
                    onClick={() => remove(a)}
                    aria-label={intl.formatMessage({ id: "agent.attach.remove", defaultMessage: "Remove attachment" })}
                    className="text-ink-muted transition hover:text-ink"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {pending && (
            <div id="agent-attachprompt" className="box-flat flex flex-wrap items-center gap-2 rounded-lg p-2 text-smycles-meta">
              <span id="agent-attachprompt-name" className="text-ink">
                {intl.formatMessage({ id: "agent.attach.prompt", defaultMessage: "Attach “{name}”?" }, { name: pending.name })}
              </span>
              {supportsFigures(pending.name, pending.type) ? (
                <>
                  <PromptChip id="agent-attachprompt-withimages" onClick={() => startUpload(pending, false)}>
                    {intl.formatMessage({ id: "agent.attach.withImages", defaultMessage: "Upload with images" })}
                  </PromptChip>
                  <PromptChip id="agent-attachprompt-withoutimages" onClick={() => startUpload(pending, true)}>
                    {intl.formatMessage({ id: "agent.attach.withoutImages", defaultMessage: "Upload without images" })}
                  </PromptChip>
                </>
              ) : (
                <PromptChip id="agent-attachprompt-textonly" onClick={() => startUpload(pending, true)}>
                  {intl.formatMessage({ id: "agent.attach.textOnly", defaultMessage: "Upload (text only)" })}
                </PromptChip>
              )}
              <PromptChip id="agent-attachprompt-cancel" onClick={() => setPending(null)}>
                {intl.formatMessage({ id: "agent.attach.cancel", defaultMessage: "Cancel" })}
              </PromptChip>
            </div>
          )}
        </div>
      )}

      {/* Hidden picker input — the visible trigger is the composer's paperclip button (AssistantDrawer). */}
      <input
        id="agent-attach-input"
        ref={inputRef}
        type="file"
        accept={RAG_DOCUMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) acceptFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
});

function PromptChip({ id, onClick, children }: { id?: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      className="box-flat rounded-lg px-2.5 py-1 text-smycles-meta text-ink transition hover:bg-ink/[0.06] hover:text-ink"
    >
      {children}
    </button>
  );
}
